/**
 * Canonical IDs for must/nice bucket storage (stable, deterministic).
 * UI continues to show display names; buckets store canonical IDs.
 */

import { PARK_KEYS, PARK_KEY_TO_DISPLAY_NAME } from "./parkKeys";
import { rideOptionsByPark } from "../data/rideOptionsByPark";
import { showOptionsByPark } from "../data/showOptionsByPark";
import { restaurantOptionsByPark } from "../data/diningOptionsByPark";

export function slugify(s) {
  if (s == null || typeof s !== "string") return "";
  return s
    .trim()
    .toLowerCase()
    .replace(/[\s''"\-–—./,()&]+/g, "_")
    .replace(/_+/g, "_")
    .replace(/^_|_$/g, "");
}

export function canonicalizeRide(parkKey, rideName) {
  const key = (parkKey || "").toLowerCase().trim();
  const slug = slugify(rideName);
  return slug ? `${key}_${slug}` : key;
}

export function canonicalizeShow(parkKey, showName) {
  const key = (parkKey || "").toLowerCase().trim();
  const slug = slugify(showName);
  return slug ? `${key}_${slug}` : key;
}

export function canonicalizeRestaurant(locationKey, restaurantName) {
  const key = (locationKey || "unscoped").toLowerCase().trim();
  const slug = slugify(restaurantName);
  return slug ? `${key}_${slug}` : key;
}

export function labelForRideId(parkKey, canonicalId) {
  if (!canonicalId || typeof canonicalId !== "string") return "";
  const displayName = PARK_KEY_TO_DISPLAY_NAME[parkKey];
  const rides = displayName ? (rideOptionsByPark[displayName] || []) : [];
  for (const ride of rides) {
    if (canonicalizeRide(parkKey, ride.name) === canonicalId) return ride.name;
  }
  return looksLikeCanonicalId(canonicalId) ? humanizeCanonicalId(canonicalId) : canonicalId;
}

export function labelForShowId(parkKey, canonicalId) {
  if (!canonicalId || typeof canonicalId !== "string") return "";
  const displayName = PARK_KEY_TO_DISPLAY_NAME[parkKey];
  const shows = displayName ? (showOptionsByPark[displayName] || []) : [];
  for (const show of shows) {
    if (canonicalizeShow(parkKey, show.name) === canonicalId) return show.name;
  }
  return looksLikeCanonicalId(canonicalId) ? humanizeCanonicalId(canonicalId) : canonicalId;
}

const RESTAURANT_LOCATION_TO_PARK = {
  mk: "Magic Kingdom",
  epcot: "EPCOT",
  hs: "Hollywood Studios",
  ak: "Animal Kingdom",
  unscoped: "Disney Springs",
};

export function labelForRestaurantId(locationKey, canonicalId) {
  if (!canonicalId || typeof canonicalId !== "string") return "";
  const parkName = RESTAURANT_LOCATION_TO_PARK[locationKey] ?? locationKey;
  const restaurants = restaurantOptionsByPark[parkName] || [];
  for (const r of restaurants) {
    if (canonicalizeRestaurant(locationKey, r.name) === canonicalId) return r.name;
  }
  return looksLikeCanonicalId(canonicalId) ? humanizeCanonicalId(canonicalId) : canonicalId;
}

const CANONICAL_ID_PREFIXES = ["mk_", "epcot_", "hs_", "ak_", "unscoped_"];

export function humanizeCanonicalId(id) {
  if (!id || typeof id !== "string") return "";
  let rest = id.trim();
  for (const prefix of CANONICAL_ID_PREFIXES) {
    if (rest.toLowerCase().startsWith(prefix)) {
      rest = rest.slice(prefix.length);
      break;
    }
  }
  const withSpaces = rest.replace(/_/g, " ").trim();
  if (!withSpaces) return id;
  const titleCased = withSpaces
    .split(/\s+/)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
  return titleCased;
}

export function resolveRideIdToLabel(id) {
  if (!id || typeof id !== "string") return "";
  if (!looksLikeCanonicalId(id)) return id;
  for (const key of [...PARK_KEYS, "unscoped"]) {
    const label = labelForRideId(key, id);
    if (label !== id) return label;
  }
  return humanizeCanonicalId(id);
}

export function resolveShowIdToLabel(id) {
  if (!id || typeof id !== "string") return "";
  if (!looksLikeCanonicalId(id)) return id;
  for (const key of [...PARK_KEYS, "unscoped"]) {
    const label = labelForShowId(key, id);
    if (label !== id) return label;
  }
  return humanizeCanonicalId(id);
}

export function resolveRestaurantIdToLabel(id) {
  if (!id || typeof id !== "string") return "";
  if (!looksLikeCanonicalId(id)) return id;
  for (const key of [...PARK_KEYS, "unscoped"]) {
    const label = labelForRestaurantId(key, id);
    if (label !== id) return label;
  }
  return humanizeCanonicalId(id);
}

export function looksLikeCanonicalId(s) {
  if (!s || typeof s !== "string") return false;
  return s.includes("_") && !/\s/.test(s) && s.length >= 3;
}

export function toCanonicalRideId(parkKey, value) {
  if (!value) return "";
  if (looksLikeCanonicalId(value)) return value;
  return canonicalizeRide(parkKey, value);
}

export function toCanonicalShowId(parkKey, value) {
  if (!value) return "";
  if (looksLikeCanonicalId(value)) return value;
  return canonicalizeShow(parkKey, value);
}

export function toCanonicalRestaurantId(locationKey, value) {
  if (!value) return "";
  if (looksLikeCanonicalId(value)) return value;
  return canonicalizeRestaurant(locationKey, value);
}

export function canonicalizeRidesBucket(bucket) {
  if (!bucket || typeof bucket !== "object") return bucket;
  const out = { ...bucket };
  for (const key of PARK_KEYS) {
    const arr = Array.isArray(out[key]) ? out[key] : [];
    const ids = arr.map((v) => toCanonicalRideId(key, v)).filter(Boolean);
    out[key] = [...new Set(ids)].sort();
  }
  if (out.unscoped != null) {
    const arr = Array.isArray(out.unscoped) ? out.unscoped : [];
    const ids = arr.map((v) => toCanonicalRideId("unscoped", v)).filter(Boolean);
    out.unscoped = [...new Set(ids)].sort();
  }
  return out;
}

export function canonicalizeShowsBucket(bucket) {
  if (!bucket || typeof bucket !== "object") return bucket;
  const out = { ...bucket };
  for (const key of PARK_KEYS) {
    const arr = Array.isArray(out[key]) ? out[key] : [];
    const ids = arr.map((v) => toCanonicalShowId(key, v)).filter(Boolean);
    out[key] = [...new Set(ids)].sort();
  }
  if (out.unscoped != null) {
    const arr = Array.isArray(out.unscoped) ? out.unscoped : [];
    const ids = arr.map((v) => toCanonicalShowId("unscoped", v)).filter(Boolean);
    out.unscoped = [...new Set(ids)].sort();
  }
  return out;
}

export function canonicalizeRestaurantsBucket(bucket) {
  if (!bucket || typeof bucket !== "object") return bucket;
  const out = { ...bucket };
  for (const key of [...PARK_KEYS, "unscoped"]) {
    const arr = Array.isArray(out[key]) ? out[key] : [];
    const ids = arr.map((v) => toCanonicalRestaurantId(key, v)).filter(Boolean);
    out[key] = [...new Set(ids)].sort();
  }
  return out;
}
